// src/utils/deleteAccount.js
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { ref, listAll, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";

async function deleteStorageFolder(prefix) {
  const folderRef = ref(storage, prefix);
  const list = await listAll(folderRef);
  const deletions = [];

  for (const item of list.items || []) {
    deletions.push(deleteObject(item).catch(() => {}));
  }

  for (const sub of list.prefixes || []) {
    deletions.push(deleteStorageFolder(sub.fullPath));
  }

  await Promise.all(deletions);
}

async function deleteProblemSubcollections(problemId) {
  const subcollections = [
    "messages",
    "files",
    "updates",
    "notes",
    "checklist",
    "events",
  ];

  for (const sub of subcollections) {
    const colRef = collection(db, "problems", problemId, sub);
    const snap = await getDocs(colRef);
    const deletes = snap.docs.map((d) => deleteDoc(d.ref).catch(() => {}));
    await Promise.all(deletes);
  }
}

async function deleteClientProblems(uid) {
  const q = query(collection(db, "problems"), where("clientId", "==", uid));
  const snap = await getDocs(q);

  for (const docSnap of snap.docs) {
    const problemId = docSnap.id;
    await deleteProblemSubcollections(problemId);
    // Delete the whole problem folder rather than named subfolders, so chat
    // attachments and evidence are removed too.
    await deleteStorageFolder(`problems/${problemId}`).catch(() => {});
    await deleteDoc(doc(db, "problems", problemId)).catch(() => {});
  }
}

async function unassignHelperProblems(uid) {
  const q = query(collection(db, "problems"), where("helperId", "==", uid));
  const snap = await getDocs(q);

  const updates = snap.docs.map((docSnap) =>
    updateDoc(docSnap.ref, {
      helperId: null,
      helperName: null,
      helperEmail: null,
      status: "open",
      assignedAt: null,
      updatedAt: serverTimestamp(),
    }).catch(() => {})
  );

  await Promise.all(updates);
}

/**
 * Deletes all user data (Firestore + Storage) for GDPR compliance.
 * @param {string} uid
 * @param {"client"|"helper"|string} role
 */
export async function deleteAccountAndData(uid, role) {
  if (!uid) throw new Error("uid is required");

  // 1) Delete user document
  await deleteDoc(doc(db, "users", uid)).catch(() => {});

  // 2) Role-specific cleanup
  if (role === "client") {
    await deleteClientProblems(uid);
  } else if (role === "helper") {
    await unassignHelperProblems(uid);
    await deleteDoc(doc(db, "helpers", uid)).catch(() => {});
  } else {
    // Attempt both just in case
    await deleteClientProblems(uid).catch(() => {});
    await deleteDoc(doc(db, "helpers", uid)).catch(() => {});
  }

  // 3) Remove helper profile even if role mis-synced
  await deleteDoc(doc(db, "helpers", uid)).catch(() => {});

  // 4) Delete user uploads
  await deleteStorageFolder(`user-uploads/${uid}`).catch(() => {});

  return true;
}

